/* eslint-disable react/no-multi-comp */
import React from 'react';
import { Redirect } from 'react-router-dom';
import * as yup from 'yup';
import marketplaceCategories from '@mooveguru/yhh-shared-config/marketplace-categories.json';
import { translate } from '../../Internationalization';
import BaseField from '../../../shared/Forms/Fields/BaseField';
import BaseForm from '../../../shared/Forms/BaseForm';
import BaseModal from '../../shared/BaseModal/BaseModal';
import captureError from '../../../utils/captureError';
import FieldEmail from '../../../shared/Forms/Fields/FieldEmail';
import FieldPhoneNumber from '../../../shared/Forms/Fields/PhoneNumber/FieldPhoneNumber';
import FieldSelect from '../../../shared/Forms/Fields/FieldSelect';
import FieldWebsite from '../../../shared/Forms/Fields/FieldWebsite';
import FormErrorMessages from '../../../shared/Forms/Messages/FormErrorMessages';
import LinkButton from '../../../shared/LinkButton';
import paths from '../../../config/local/paths';
import SubmitButton from '../../../shared/Forms/Inputs/SubmitButton';
import { withOfferService } from '../../../service-container';
import { withProperty } from '../PropertyProvider';
import { withTheme } from '../../../shared/ThemeProvider';
import * as validators from '../../../shared/validators';

const INITIAL_VALUES = {
	category: '',
	vendorEmail: '',
	vendorName: '',
	vendorPhone: '',
	website: '',
};

const validationSchema = yup.object().shape({
	category: validators.required('Category is required.'),
	vendorEmail: validators.email,
	vendorName: validators.required('Vendor Name is required.'),
	vendorPhone: validators.phoneNumber.required('Phone Number is required.'),
	website: yup
		.string()
		.matches(
			validators.urlRegExp,
			translate('global.validator.invalid_url_format')
		)
		.required('Website is required.'),
});

/**
 * @typedef {{ category: string, vendorEmail: string, vendorName: string, vendorPhone: string, website: string }} PreferredVendorFormValues
 * @typedef {{ category: string, email: string | null, headline?: string, id: string, logoUrl?: null, name: string, phoneNumber: string | null, websiteUrl: string | null }} PreferredVendor
 * @typedef {{ id: string, requiresConfirmation?: boolean, vendor?: PreferredVendor | null }} AddPreferredVendorResult
 * @typedef {{ errors?: string[] }} ServiceError
 */

/**
 * @param {string} category
 * @returns {string}
 */
function getMarketplaceCategoryLabel(category) {
	/** @type {Record<string, string>} */
	const categoryLabels = marketplaceCategories;

	return categoryLabels[category] ?? category;
}

/**
 * @param {unknown} error
 * @returns {string[]}
 */
function getServiceErrorMessages(error) {
	const fallbackMessage = 'Unable to add preferred vendor. Please try again.';
	const serviceError = /** @type {ServiceError} */ (error);

	return serviceError.errors?.length
		? serviceError.errors
		: [fallbackMessage];
}

/**
 * @param {{ category: string, onClose: () => void, onConfirm: () => void, tenantName: string, vendorName: string }} props
 * @returns {JSX.Element}
 */
function PreferredVendorConfirmation(props) {
	const categoryLabel = getMarketplaceCategoryLabel(props.category);

	return (
		<BaseModal onClose={props.onClose}>
			<section className="w-full max-w-xl px-4 py-2">
				<h2 className="h-ms-5 mb-4 text-center">
					{'Confirm Preferred Vendor'}
				</h2>
				<p className="mb-4">
					{'You have identified '}
					<span className="font-bold">{props.vendorName}</span>
					{' as your preferred Home Pro for '}
					<span className="font-bold">{categoryLabel}</span>
					{'.'}
				</p>
				<p className="mb-6">
					{props.tenantName}
					{
						' will help you manage and maintain your home by alerting '
					}
					<span className="font-bold">{props.vendorName}</span>
					{' that you have made them a preferred Home Pro. '}
					{props.vendorName}
					{' will be invited to join the '}
					{props.tenantName}
					{
						' network to help ensure your home is properly managed and maintained.'
					}
				</p>
				<footer className="button-group w-min mx-auto">
					<button
						className="button button-outline button-lg"
						onClick={props.onClose}
						type="button"
					>
						{'Cancel'}
					</button>
					<button
						className="button button-primary button-lg"
						onClick={props.onConfirm}
						type="button"
					>
						{'Accept and Add'}
					</button>
				</footer>
			</section>
		</BaseModal>
	);
}

/**
 * @param {{ onClose: () => void, onConfirm: () => void, vendor: { category: string, email: string | null, name: string, websiteUrl: string | null } }} props
 * @returns {JSX.Element}
 */
function ExistingVendorConfirmation(props) {
	const categoryLabel = getMarketplaceCategoryLabel(props.vendor.category);

	return (
		<BaseModal onClose={props.onClose}>
			<section className="w-full max-w-xl px-4 py-2">
				<h2 className="h-ms-5 mb-4 text-center">
					{'Vendor Already In System'}
				</h2>
				<p className="mb-4">
					{
						'This vendor is already in our system. Do you want to add this vendor to your preferred Home Pros?'
					}
				</p>
				<div className="mb-6 rounded border border-grey-200 px-4 py-3">
					<p>
						<span className="font-bold">{'Name:'}</span>{' '}
						{props.vendor.name}
					</p>
					<p>
						<span className="font-bold">{'Email:'}</span>{' '}
						{props.vendor.email || 'N/A'}
					</p>
					<p>
						<span className="font-bold">{'Website:'}</span>{' '}
						{props.vendor.websiteUrl || 'N/A'}
					</p>
					<p>
						<span className="font-bold">{'Category:'}</span>{' '}
						{categoryLabel}
					</p>
				</div>
				<footer className="button-group w-min mx-auto">
					<button
						className="button button-outline button-lg"
						onClick={props.onClose}
						type="button"
					>
						{'Cancel'}
					</button>
					<button
						className="button button-primary button-lg"
						onClick={props.onConfirm}
						type="button"
					>
						{'Yes, Add'}
					</button>
				</footer>
			</section>
		</BaseModal>
	);
}

/* eslint-disable complexity */
/**
 * @param {any} props
 * @returns {JSX.Element}
 */
function AddPreferredVendor(props) {
	const [createdVendor, setCreatedVendor] = React.useState(
		/** @type {PreferredVendor | null} */ (null)
	);
	const [existingVendorDetails, setExistingVendorDetails] = React.useState(
		/** @type {PreferredVendor | null} */ (null)
	);
	const [errorMessage, setErrorMessage] = React.useState(
		/** @type {string[]} */ ([])
	);
	const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
	const [isExistingVendorConfirmOpen, setIsExistingVendorConfirmOpen] =
		React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);
	const [pendingValues, setPendingValues] = React.useState(
		/** @type {PreferredVendorFormValues | null} */ (null)
	);
	const propertyId = props.match?.params?.propertyId ?? '';
	const homeProsPath = paths.app.homeowner.properties.scoped.homepros.replace(
		':propertyId',
		propertyId
	);
	const initialValues = {
		...INITIAL_VALUES,
		category: props.location?.state?.category ?? '',
	};
	const tenantName = props.theme?.appTitle?.trim() || 'HomeKeepr';

	const handleCloseConfirmation = React.useCallback(() => {
		if (isSaving) {
			return;
		}

		setIsConfirmOpen(false);
	}, [isSaving]);

	const handleCloseExistingVendorConfirmation = React.useCallback(() => {
		if (isSaving) {
			return;
		}

		setIsExistingVendorConfirmOpen(false);
		setExistingVendorDetails(null);
	}, [isSaving]);

	const handleFormSubmit = React.useCallback(
		/**
		 * @param {PreferredVendorFormValues} values
		 * @param {(isSubmitting: boolean) => void} setSubmitting
		 * @returns {void}
		 */
		(values, setSubmitting) => {
			setErrorMessage([]);
			setPendingValues(values);
			setIsConfirmOpen(true);
			setSubmitting(false);
		},
		[]
	);

	const handleConfirmAdd = React.useCallback(async () => {
		if (!pendingValues || isSaving) {
			return;
		}

		setIsConfirmOpen(false);
		setIsSaving(true);
		setErrorMessage([]);

		try {
			const result = await props.offerService.addPreferredVendor(
				propertyId,
				pendingValues
			);

			if (result.requiresConfirmation) {
				setExistingVendorDetails(result.vendor);
				setIsExistingVendorConfirmOpen(true);

				return;
			}

			const savedVendor = result.vendor ?? {
				category: pendingValues.category,
				email: pendingValues.vendorEmail,
				id: result.id,
				name: pendingValues.vendorName,
				phoneNumber: pendingValues.vendorPhone,
				websiteUrl: pendingValues.website,
			};

			setCreatedVendor({
				category: savedVendor.category,
				email: savedVendor.email,
				headline: savedVendor.name,
				id: savedVendor.id,
				logoUrl: null,
				name: savedVendor.name,
				phoneNumber: savedVendor.phoneNumber,
				websiteUrl: savedVendor.websiteUrl,
			});
		} catch (error) {
			captureError(error);
			setErrorMessage(getServiceErrorMessages(error));
		} finally {
			setIsSaving(false);
			setIsConfirmOpen(false);
		}
	}, [isSaving, pendingValues, propertyId, props.offerService]);

	const handleConfirmExistingVendorAdd = React.useCallback(async () => {
		if (!pendingValues || isSaving) {
			return;
		}

		setIsExistingVendorConfirmOpen(false);
		setIsSaving(true);
		setErrorMessage([]);

		try {
			const result = await props.offerService.addPreferredVendor(
				propertyId,
				pendingValues,
				{ confirmExisting: true }
			);

			const savedVendor = result.vendor ??
				existingVendorDetails ?? {
					category: pendingValues.category,
					email: pendingValues.vendorEmail,
					id: result.id,
					name: pendingValues.vendorName,
					phoneNumber: pendingValues.vendorPhone,
					websiteUrl: pendingValues.website,
				};

			setCreatedVendor({
				category: savedVendor.category,
				email: savedVendor.email,
				headline: savedVendor.name,
				id: savedVendor.id,
				logoUrl: null,
				name: savedVendor.name,
				phoneNumber: savedVendor.phoneNumber,
				websiteUrl: savedVendor.websiteUrl,
			});
		} catch (error) {
			captureError(error);
			setErrorMessage(getServiceErrorMessages(error));
		} finally {
			setIsSaving(false);
			setIsExistingVendorConfirmOpen(false);
			setExistingVendorDetails(null);
		}
	}, [
		existingVendorDetails,
		isSaving,
		pendingValues,
		propertyId,
		props.offerService,
	]);

	if (createdVendor) {
		return (
			<Redirect
				to={{
					pathname: homeProsPath,
					state: {
						message: ['Preferred vendor saved successfully.'],
						preferredVendor: createdVendor,
					},
				}}
			/>
		);
	}

	return (
		<React.Fragment>
			<section className="app-block-list">
				<h1 className="hl-ms-6 mt-10 mb-6">
					{'Find / Create Your Preferred Vendor'}
				</h1>

				<BaseForm
					className="mb-10"
					initialValues={initialValues}
					onSubmit={handleFormSubmit}
					validationSchema={validationSchema}
				>
					<fieldset className="grid grid-cols-1 gap-6 mb-8 max-w-4xl">
						<legend className="h-ms-5 mb-2">
							{'Add required information for vendor'}
						</legend>

						<BaseField
							label="Name"
							name="vendorName"
							placeholder="Vendor name"
							required={true}
						/>
						<FieldWebsite
							label="Website"
							name="website"
							placeholder="https://example.com"
							required={true}
						/>
						<FieldEmail
							label="Email"
							name="vendorEmail"
							placeholder="vendor@example.com"
							required={true}
						/>
						<FieldPhoneNumber
							label="Phone"
							name="vendorPhone"
							required={true}
							type="work"
						/>
						<FieldSelect
							label="Choose Category"
							name="category"
							noDefaultOption={true}
							required={true}
						>
							{Object.entries(marketplaceCategories).map(
								([value, label]) => (
									<option key={value} value={value}>
										{label}
									</option>
								)
							)}
						</FieldSelect>
					</fieldset>

					<FormErrorMessages messages={errorMessage} />

					<footer className="button-group w-min mt-6">
						<SubmitButton disabled={isSaving}>
							{'Add and Mark Favorite'}
						</SubmitButton>
						<LinkButton size="large" to={homeProsPath}>
							{'Cancel'}
						</LinkButton>
					</footer>
				</BaseForm>
			</section>

			{isConfirmOpen && pendingValues ? (
				<PreferredVendorConfirmation
					category={pendingValues.category}
					onClose={handleCloseConfirmation}
					onConfirm={handleConfirmAdd}
					tenantName={tenantName}
					vendorName={pendingValues.vendorName}
				/>
			) : null}
			{isExistingVendorConfirmOpen && existingVendorDetails ? (
				<ExistingVendorConfirmation
					onClose={handleCloseExistingVendorConfirmation}
					onConfirm={handleConfirmExistingVendorAdd}
					vendor={existingVendorDetails}
				/>
			) : null}
		</React.Fragment>
	);
}

export default withOfferService(withTheme(withProperty(AddPreferredVendor)));
